import React from "react";

/**
 * Watchlist / avoid toggles shared by every player-list surface (mock auction
 * list, Team Builder list, TierViewer).
 *
 * TOUCH TARGET: these are real <button>s carrying `.list-toggle`, which grows to
 * a 44x44 minimum at mobile widths (see common.css). The HIT AREA grows — the
 * glyph font-size never does. Desktop keeps its current density: padding 4px is
 * cancelled by margin -4px, so the button occupies exactly the space the old
 * <span> did while still being 8px easier to hit.
 *
 * No negative margins at mobile: two 44px targets sitting side by side would
 * overlap, and the later one in the DOM would silently swallow taps meant for
 * the other. Spacing between toggles comes from the row's flex gap instead.
 *
 * Every toggle stops propagation on both click and pointerdown, so a tap can
 * never fall through to the row (which opens the player card / expands the row
 * / selects a nomination).
 */
function stop(e) { e.stopPropagation(); }

export function StarToggle({ name, watchlist, onToggleWatchlist, size = 14 }) {
  if (!onToggleWatchlist) return null;
  const on = watchlist?.has(name);
  return (
    <button
      type="button"
      className="list-toggle"
      onPointerDown={stop}
      onClick={e => { e.stopPropagation(); onToggleWatchlist(name); }}
      title={on ? "Remove from watchlist" : "Star — add to watchlist"}
      aria-label={on ? `Unstar ${name}` : `Star ${name}`}
      aria-pressed={!!on}
      style={{ fontSize: size, color: on ? "#eab308" : "#334155" }}
    >{on ? "★" : "☆"}</button>
  );
}

// The 🚫 emoji carries its own color and ignores `color`, so the off state is
// greyed with a filter instead.
export function AvoidToggle({ name, avoids, onToggleAvoid, size = 13 }) {
  if (!onToggleAvoid) return null;
  const on = avoids?.has(name);
  return (
    <button
      type="button"
      className="list-toggle list-toggle-avoid"
      onPointerDown={stop}
      onClick={e => { e.stopPropagation(); onToggleAvoid(name); }}
      title={on ? "Remove from avoids" : "Avoid — do not draft"}
      aria-label={on ? `Stop avoiding ${name}` : `Avoid ${name}`}
      aria-pressed={!!on}
      style={{ fontSize: size, filter: on ? "none" : "grayscale(1) opacity(0.4)" }}
    >{"🚫"}</button>
  );
}

// Small inline marker before an avoided player's name, so the row reads as
// "don't draft" at a glance without hunting for the toggle. Not interactive.
export function AvoidMark({ name, avoids, size = 10 }) {
  if (!avoids?.has(name)) return null;
  return <span style={{ fontSize: size, lineHeight: 1, flexShrink: 0, marginRight: 3 }}>{"🚫"}</span>;
}
